import { getExtension, getExtensionValue, parseReference } from '@medplum/core';
import type { CodeableConcept, Communication } from '@medplum/fhirtypes';
import { z } from 'zod';
import {
  CHAT_SURFACE_SYSTEM,
  EFAX_ORIGINATING_FAX_NUMBER_EXTENSION_URL,
  EXTENSIONS,
  FAX_DIRECTION_SYSTEM,
} from './constants';
import {
  VALID_CHAT_SURFACES,
  VALID_CONVERSATION_TYPES,
  VALID_SENDER_TYPES,
  type ChatSurface,
  type ConversationType,
  type MessageSenderType,
  type PatientpediaChartsData,
} from './types';

// --- Message utilities (child Communications with partOf) ---

/** Extract the sender type from a message Communication's category. */
export function getMessageSenderType(comm: Communication): MessageSenderType | null {
  const value = comm.category?.[0]?.text;
  if (value && VALID_SENDER_TYPES.has(value)) return value as MessageSenderType;
  return null;
}

/** `Communication.medium` value recording which app surface a chat message came from. */
export const buildChatSurfaceMedium = (surface: ChatSurface): CodeableConcept[] => [
  { coding: [{ system: CHAT_SURFACE_SYSTEM, code: surface }] },
];

/**
 * Extract the chat surface from a message Communication's medium. `null` when absent or
 * unrecognized — pre-surface messages and old clients; callers choose the fallback.
 */
export const getChatSurface = (comm: Communication): ChatSurface | null => {
  const code = comm.medium
    ?.flatMap((m) => m.coding ?? [])
    .find((c) => c.system === CHAT_SURFACE_SYSTEM && c.code && VALID_CHAT_SURFACES.has(c.code))?.code;
  return code ? (code as ChatSurface) : null;
};

/**
 * Index of the payload entry carrying the message body, or `-1` when there is none.
 *
 * The body is written first, so this is index 0 for every message that has text — but the index
 * is located rather than assumed, because a message may also carry non-text payload entries
 * (attachments, as `contentReference`) and an attachment-only message has no text entry at all.
 * Readers that hardcode index 0 silently read an attachment as the body, and writers that patch
 * `/payload/0` silently overwrite one.
 */
export const findTextPayloadIndex = (comm: Communication): number =>
  comm.payload?.findIndex((entry) => entry.contentString !== undefined) ?? -1;

/** Extract the message content from a Communication's payload. Empty for an attachment-only message. */
export function getMessageContent(comm: Communication): string {
  const index = findTextPayloadIndex(comm);
  return index === -1 ? '' : (comm.payload?.[index]?.contentString ?? '');
}

/** Extract the conversation ID from a message Communication's partOf reference. */
export function getMessageConversationId(comm: Communication): string | null {
  const partOf = comm.partOf?.[0];
  if (!partOf?.reference) return null;
  const [resourceType, id] = parseReference(partOf);
  return resourceType === 'Communication' ? id : null;
}

const patientpediaChartDataItemSchema = z.object({
  type: z.enum(['line', 'bar']),
  title: z.string(),
  xAxisLabel: z.string(),
  yAxisLabel: z.string(),
  points: z.array(z.object({ x: z.string(), y: z.number(), y2: z.number().optional() })),
  seriesLabels: z
    .object({
      y: z.string(),
      y2: z.string(),
    })
    .optional(),
  referenceRange: z
    .object({
      low: z.number().nullable(),
      high: z.number().nullable(),
      unit: z.string(),
    })
    .optional(),
});

const patientpediaChartsDataSchema = z.array(patientpediaChartDataItemSchema);

/** Parse charts data from a message Communication's extension. Returns null if not present or malformed. */
export function getMessageChartsData(comm: Communication): PatientpediaChartsData | null {
  const value = getExtensionValue(comm, EXTENSIONS.CHARTS_DATA) as string | undefined;
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as { version: number; charts: unknown };
    if (parsed.version !== 1) return null;
    const result = patientpediaChartsDataSchema.safeParse(parsed.charts);
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

// --- Fax utilities ---

export type FaxDirection = 'inbound' | 'outbound';

/**
 * The fax direction of a Communication, read from the `category[]` coding carrying the
 * fax-direction system: `inbound` for faxes created by Medplum's `$receive-efax` operation,
 * `outbound` for faxes we send. Null when the Communication is not a fax (or carries an
 * unknown direction code).
 */
export function getFaxDirection(comm: Communication): FaxDirection | null {
  for (const category of comm.category ?? []) {
    for (const coding of category.coding ?? []) {
      if (coding.system === FAX_DIRECTION_SYSTEM && (coding.code === 'inbound' || coding.code === 'outbound')) {
        return coding.code;
      }
    }
  }
  return null;
}

/**
 * Whether a Communication is an inbound fax created by Medplum's `$receive-efax` operation:
 * some `category[]` entry has a coding carrying both the fax-direction system and the
 * `inbound` code.
 */
export function isInboundFax(comm: Communication): boolean {
  return getFaxDirection(comm) === 'inbound';
}

/**
 * The sender's fax number from an inbound fax Communication's eFax extension.
 * Raw extension value — 11 digits with a leading `1` and no `+` prefix.
 */
export function getOriginatingFaxNumber(comm: Communication): string | undefined {
  return getExtension(comm, EFAX_ORIGINATING_FAX_NUMBER_EXTENSION_URL)?.valueString;
}

// --- Conversation utilities (root Communications without partOf) ---

/** Extract the conversation type from a root Communication's category. */
export function getConversationType(comm: Communication): ConversationType | null {
  const value = comm.category?.[0]?.text;
  if (value && VALID_CONVERSATION_TYPES.has(value)) return value as ConversationType;
  return null;
}

/** Extract the conversation title from a root Communication's topic. */
export function getConversationTitle(comm: Communication): string | null {
  return comm.topic?.text ?? null;
}

/** Check if a conversation Communication is active (status === 'in-progress'). */
export function isConversationActive(comm: Communication): boolean {
  return comm.status === 'in-progress';
}
